import axios from "axios";
import { config, authBypassPaths } from "../config/env";
import { authStore, getTabScope, getTokenKey } from "../stores/auth.store";
import { ENDPOINTS } from "./Endpoints";

const axiosInstance = axios.create({
  baseURL: config.apiBaseUrl,
  headers: {
    "Content-Type": "application/json",
  },
  withCredentials: true,
  timeout: 30000,
});

axiosInstance.interceptors.request.use((requestConfig) => {
  const token = authStore.token;
  const requestPath = requestConfig.url?.split("?")[0];

  const isBypassPath = authBypassPaths.includes(requestPath);
  const shouldBypassAuth = isBypassPath || !token;

  if (!shouldBypassAuth) {
    requestConfig.headers.Authorization = `Bearer ${token}`;
  } else {
    delete requestConfig.headers.Authorization;
  }

  return requestConfig;
});

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) prom.reject(error);
    else prom.resolve(token);
  });
  failedQueue = [];
};

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Do not attempt token refresh / redirect for unauthenticated endpoints
    // (login, admin login, register, refresh, etc.). A 401 from these is an
    // expected auth failure that should surface to the caller as an error,
    // not trigger a refresh-and-redirect loop that reloads the page.
    const requestPath = originalRequest?.url?.split("?")[0];
    if (!originalRequest || authBypassPaths.includes(requestPath)) {
      return Promise.reject(error);
    }

    // Maintenance mode: backend returns 503 for non-admin requests.
    if (error.response?.status === 503) {
      const currentPath = window.location.pathname;
      if (currentPath !== "/maintenance") {
        window.location.href = "/maintenance";
      }
      return Promise.reject(error);
    }

    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(() => axiosInstance(originalRequest));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      // Refresh the session this tab is signed in as. Without the scope the
      // server would read whichever login happened last in any tab.
      const scope = getTabScope();

      try {
        if (!scope) {
          throw new Error("No session to refresh in this tab");
        }

        const response = await axiosInstance.post(ENDPOINTS.AUTH.REFRESH_TOKENS, {
          scope,
        });
        const newAccessToken =
          response.data?.data?.accessToken || response.data?.accessToken;

        if (!newAccessToken) {
          throw new Error("No access token in refresh response");
        }

        // Keep the token in this tab's scope, and in whichever storage it
        // already lived in (tab-only or remembered).
        const roleKey = getTokenKey(scope);
        const remembered =
          !sessionStorage.getItem(roleKey) && Boolean(localStorage.getItem(roleKey));
        authStore.setToken(newAccessToken, remembered, scope);
        processQueue(null, newAccessToken);
        return axiosInstance(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError);
        authStore.reset();
        window.location.href = "/auth/login";
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

export default axiosInstance;
